import { Dialog, DialogContent, DialogTitle } from "@mzc-pdc/ui";
import { Children, cloneElement, useCallback, useMemo, useRef, useState } from "react";
import theme from "@styles/theme";

const StickyHeaderDialog = ({ children, ...props }) => {
    const [isSticky, setIsSticky] = useState(false);
    const internalRef = useRef(null);
    const listenerAttachedRef = useRef(false); // 중복 등록 방지

    const checkSticky = useCallback(() => {
        if (!internalRef.current) return;

        const contentTop = internalRef.current.getBoundingClientRect().top;
        const firstChildTop = internalRef.current.children[0]?.getBoundingClientRect().top;

        if (contentTop - firstChildTop <= 0) {
            setIsSticky(false);
        } else {
            setIsSticky(true);
        }
    }, []);

    const mergeWithInternalRef = useCallback(
        (originalRef) => {
            return (element) => {
                if (internalRef.current && listenerAttachedRef.current) {
                    internalRef.current.removeEventListener("scroll", checkSticky);
                    listenerAttachedRef.current = false;
                }

                internalRef.current = element;

                if (element && ["expandable"].includes(props?.variant)) {
                    element.addEventListener("scroll", checkSticky);
                    listenerAttachedRef.current = true;
                }

                if (originalRef) {
                    if (typeof originalRef === "function") {
                        originalRef(element);
                    } else {
                        originalRef.current = element;
                    }
                }
            };
        },
        [checkSticky, props?.variant],
    );

    const stickyTitleStyle = {
        zIndex: 1,
        boxShadow: `0 1px 0 ${theme.palette.greySecondary[100]}`,
    };

    const childrenWithRef = useMemo(
        () =>
            Children.map(children, (child) => {
                if (!child) return null;

                if (["expandable"].includes(props?.variant)) {
                    if (child.type === DialogContent) {
                        return cloneElement(child, {
                            ...child.props,
                            ref: mergeWithInternalRef(child.ref),
                        });
                    }
                }

                return child;
            }),
        [children, props?.variant, mergeWithInternalRef],
    );

    const newEnhancedChildren = useMemo(
        () =>
            Children.map(childrenWithRef, (child) => {
                if (!child) return null;

                if (["expandable"].includes(props?.variant)) {
                    if (child.type === DialogTitle && isSticky) {
                        return cloneElement(child, {
                            ...child.props,
                            sx: {
                                ...stickyTitleStyle,
                                ...(child.props.sx || {}),
                            },
                        });
                    }
                }

                return child;
            }),
        [childrenWithRef, isSticky, props?.variant],
    );

    return <Dialog {...props}>{newEnhancedChildren}</Dialog>;
};

export default StickyHeaderDialog;
